import { camelize } from 'casing'
import type {
  CloudMarketplace,
  Product,
  ProductDimension,
} from '../../../productsListing/types'
import type {
  MarketplaceFieldMapping,
  MarketplaceFieldMappingMetadata,
  MarketplaceTemplateData,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../types/mappingManager'
import { awsReferralFieldToFormMetadataKey } from '../../types'
import {
  getDimensionsBasedOnProductType,
  getDimensionsFromReferralMapping,
  mergeMappedDimensionsWithProductDimensions,
} from './dimensionHelper'
import { getPlansFromReferralMapping } from './microsoftPlanHelper'
import { installmentRowToFormKeySnake } from './installmentHelper'

export const getOfferDetailInfo = (
  referralObject: Record<string, unknown>,
  cloud?: CloudMarketplace
): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  const pickString = (sfField: string, formKey: string) => {
    const v = referralObject[sfField]
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      out[formKey] = v
    }
  }

  if (cloud === 'AWS') {
    pickString('Labra__Account_Id__c', 'aws_account_id')
    pickString('Labra__Company_Name__c', 'company_name')
    pickString('Labra__Offer_Name__c', 'private_offer_name')
    pickString('Labra__EULA_Type__c', 'eula_type')
    pickString('Labra__Entitlement_Option__c', 'entitlement_option')
    pickString('Labra__Subscription_End_Date__c', 'subscription_end_date')
    pickString('Labra__Subscription_Start_Date__c', 'agreement_start_date')
    pickString('Labra__Renewal_Type__c', 'renewalChannel')
    pickString('Labra__Offer_Expiration_Date__c', 'offer_expiration_date')
    pickString('Labra__Custom_Email_Message__c', 'emailCustomMessage')
    pickString('Labra__EULA_Attachment__c', 'eula_file')

    const contractMonthsRaw = referralObject.Labra__Contract_Duration_Months__c
    if (
      contractMonthsRaw !== undefined &&
      contractMonthsRaw !== null &&
      String(contractMonthsRaw).trim() !== ''
    ) {
      const n = Number(contractMonthsRaw)
      if (!Number.isNaN(n)) {
        out.service_length = n
      }
    }
    if (referralObject?.Labra__Subscription_Start_Date__c) {
      out.variableStartDate = false
    }
    const isFlexiblePaymentTerms =
      !!referralObject?.Labra__Installment_Amount__c ||
      !!referralObject?.Labra__Installment_Date__c
    if (isFlexiblePaymentTerms) {
      out.installments = [
        {
          amount: referralObject?.Labra__Installment_Amount__c,
          paymentDate: referralObject?.Labra__Installment_Date__c,
        },
      ]
    }

    const isRenewal = !!referralObject?.Labra__Renewal_Type__c
    if (isRenewal) {
      out.renewal = true
      out.renewalChannel = referralObject?.Labra__Renewal_Type__c
    }

    out.flexiblePaymentTerms = isFlexiblePaymentTerms
  } else if (cloud === 'AZURE') {
    pickString('Labra__MSFT_Billing_Account_ID__c', 'billing_account_id')
    pickString('Labra__Company_Name__c', 'company_name')
    pickString('Labra__Offer_Name__c', 'private_offer_name')
    pickString('Labra__EULA_Type__c', 'eula_type')
    pickString('Labra__EULA_Attachment__c', 'eula_files')
    pickString('Labra__Custom_Email_Message__c', 'email_custom_message')
    pickString('Labra__Offer_Expiration_Date__c', 'accept_by')
    pickString('Labra__Renewal_Type__c', 'existing_contract_renewal')

    out.existingContractRenewal =
      referralObject?.Labra__Renewal_Type__c === 'Yes'
  }

  return {
    ...out,
  }
}

export const getMetadataFromReferralMapping = (
  referralObject: Record<string, unknown>,
  metadataMapping?: Record<string, MarketplaceFieldMappingMetadata>
): { key: string; value: string }[] | undefined => {
  if (!metadataMapping || Object.keys(metadataMapping).length === 0) {
    return undefined
  }
  const metaData = Object.values(metadataMapping)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map(meta => {
      const fromReferral = referralObject[meta.referralField]
      const hasReferral =
        fromReferral !== undefined &&
        fromReferral !== null &&
        String(fromReferral).trim() !== ''
      const value = hasReferral
        ? String(fromReferral)
        : Array.isArray(meta.defaultValue)
          ? meta.defaultValue.join(',')
          : String(meta.defaultValue ?? '')
      return { key: meta.key ?? '', value }
    })
    .filter(row => row.key !== '' || row.value !== '')
  return metaData.length > 0 ? metaData : undefined
}

export const mappingReferralObjectToForm = (
  productId: string,
  referralObject: Record<string, unknown> | undefined | null,
  dimensions?: Record<string, unknown>[] | null,
  mappingTemplate?: MarketplaceTemplateData,
  product?: Product,
  cloud?: CloudMarketplace
): Record<string, unknown> => {
  if (!referralObject) {
    return {}
  }

  const fieldMappings = mappingTemplate?.templateData?.fieldMappings
  const out: Record<string, unknown> = {
    ...getOfferDetailInfo(referralObject, cloud),
  }

  const metaData = getMetadataFromReferralMapping(
    referralObject,
    fieldMappings?.metadata?.metadataMapping
  )
  if (metaData) {
    out.metaData = metaData
  }
  if (cloud === 'AWS') {
    const mappedDimensions = getDimensionsFromReferralMapping(
      productId,
      dimensions,
      fieldMappings,
      product?.productDimensions,
      product,
      false
    )
    const dimensionsList = (
      product ? getDimensionsBasedOnProductType(product as Product, true) : []
    ) as ProductDimension[]

    const mergedDimensions = mergeMappedDimensionsWithProductDimensions(
      mappedDimensions,
      dimensionsList
    )
    if (mergedDimensions) {
      out.dimensions = mergedDimensions
    }
  } else if (cloud === 'AZURE') {
    const mappedPlans = getPlansFromReferralMapping(
      productId,
      dimensions,
      fieldMappings,
      product?.plans
    )
    if (mappedPlans) {
      out.plans = mappedPlans
    }
  }
  return out
}

type FieldMappingFlags = { isHidden?: boolean; isReadonly?: boolean }

export const toFieldMetadataEntry = (
  field?: FieldMappingFlags | null
): { hidden: boolean; readOnly: boolean } => ({
  hidden: Boolean(field?.isHidden),
  readOnly: Boolean(field?.isReadonly),
})

/** Salesforce referral API name → private-offer form keys (snake_case; then camelized for Redux). */
export const azureReferralFieldToFormMetadataKey: Record<string, string> = {
  Labra__MSFT_Billing_Account_ID__c: 'azure_billing_account_id',
  Labra__Company_Name__c: 'company_name',
  Labra__Custom_Email_Message__c: 'email_custom_message',
  Labra__EULA_Attachment__c: 'eula_files',
  Labra__EULA_Type__c: 'eula_type',
  Labra__Offer_Expiration_Date__c: 'accept_by',
  Labra__Offer_Name__c: 'private_offer_name',
  Labra__Renewal_Type__c: 'existing_contract_renewal',
  Labra__Stakeholders__c: 'stakeholders',
  Labra__Metadata_Hide__c: 'metadata',
}

const awsTopLevelTemplateFieldMetadata: Array<{
  templateKey: keyof MarketplaceTemplatesDataFieldMappings
  formKey: string
}> = [
  { templateKey: 'awsAccountNumber', formKey: 'aws_account_id' },
  { templateKey: 'companyName', formKey: 'company_name' },
  { templateKey: 'recipient', formKey: 'offer_recipients' },
  { templateKey: 'customEmailMessage', formKey: 'email_custom_message' },
  { templateKey: 'contractDuration', formKey: 'service_length' },
  { templateKey: 'entitlementOption', formKey: 'entitlement_option' },
  { templateKey: 'renewal', formKey: 'renewal' },
  { templateKey: 'eula', formKey: 'eula_type' },
  { templateKey: 'customEula', formKey: 'eula_file' },
]

const azureTopLevelTemplateFieldMetadata: Array<{
  templateKey: keyof MarketplaceTemplatesDataFieldMappings
  formKey: string
}> = [
  { templateKey: 'billingAccountId', formKey: 'billing_account_id' },
  { templateKey: 'companyName', formKey: 'company_name' },
  { templateKey: 'customEmailMessage', formKey: 'email_custom_message' },
  { templateKey: 'eula', formKey: 'eula_type' },
  { templateKey: 'customEula', formKey: 'eula_files' },
  { templateKey: 'offerExpirationDate', formKey: 'accept_by' },
  {
    templateKey: 'existingContractRenewal',
    formKey: 'existing_contract_renewal',
  },
]

/**
 * Builds field visibility/editability metadata from the marketplace mapping template.
 * Keys are snake_case and then camelized when stored (e.g. aws_account_id → awsAccountId).
 */
export const getFieldMetadataFromTemplate = (
  template: MarketplaceTemplatesDataFieldMappings | undefined | null,
  _referralObject?: Record<string, unknown>,
  cloud?: CloudMarketplace
): Record<string, unknown> => {
  const out: Record<string, unknown> = {}

  const assign = (
    formKey: string | undefined,
    field?: FieldMappingFlags | null
  ) => {
    if (!formKey) {
      return
    }
    out[formKey] = toFieldMetadataEntry(field)
  }

  if (!template) {
    return camelize(out) as Record<string, unknown>
  }

  const topLevelTemplateFieldMetadata =
    cloud === 'AWS'
      ? awsTopLevelTemplateFieldMetadata
      : azureTopLevelTemplateFieldMetadata

  for (const { templateKey, formKey } of topLevelTemplateFieldMetadata) {
    const field = template[templateKey] as MarketplaceFieldMapping | undefined
    if (field && typeof field === 'object') {
      assign(formKey, field)
    }
  }

  const fieldsMapping = template.fields?.fieldsMapping
  if (fieldsMapping) {
    for (const mapping of Object.values(fieldsMapping)) {
      const referralField =
        typeof mapping?.referralField === 'string' ? mapping.referralField : ''
      const formKey =
        cloud === 'AWS'
          ? awsReferralFieldToFormMetadataKey[referralField]
          : azureReferralFieldToFormMetadataKey[referralField]
      if (formKey) {
        assign(formKey, mapping)
      }
    }
  }

  const metadataMapping = template.metadata?.metadataMapping
  if (metadataMapping) {
    const metadata = Object.values(metadataMapping).map(meta => {
      return {
        key: meta.key ?? '',
        value: meta.defaultValue ?? '',
        hidden: meta.isHidden,
        readOnly: meta.isReadonly,
      }
    })
    out.metadata = metadata
  }

  if (cloud === 'AWS') {
    const installmentsMapping = template.installments?.installmentsMapping
    if (installmentsMapping) {
      for (const [installmentKey, inst] of Object.entries(
        installmentsMapping
      )) {
        assign(installmentRowToFormKeySnake(inst, installmentKey), inst)
      }
    }
  }

  const recipientSourceMapping = (_referralObject?.Labra__Recipient_Source__c ||
    []) as Record<string, unknown>[]
  if (recipientSourceMapping.length > 0) {
    out.offerRecipientsList = {
      offerRecipients: recipientSourceMapping.map(recipient => ({
        firstName: recipient?.FirstName,
        lastName: recipient?.LastName,
        email: recipient?.email,
        title: recipient?.Title,
      })),
    }
  }

  return camelize(out) as Record<string, unknown>
}
